import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Scroll olayını dinle ve state güncelle
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sayfa değiştiğinde mobil menüyü kapat
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  const menuItems = [
    { path: '/', label: 'Ana Sayfa' },
    { path: '/about', label: 'Hakkımızda' },
    { path: '/contact', label: 'İletişim' },
    { path: '/projects', label: 'Projelerimiz' },
  ];

  // Pasif link tıklama engelleyici
  const handleStoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-primary-900/95 backdrop-blur-md shadow-lg py-3' 
          : 'bg-primary-900/80 backdrop-blur-sm py-5'
      }`}
    >
      {/* Üst kısımdaki gradient */}
      {!scrolled && (
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-primary-950/60 to-transparent -z-10 hidden lg:block pointer-events-none" />
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* LOGO */}
          <Link to="/" className="group flex items-center space-x-3 z-50">
            <img 
              src="/lotus-sport-logo.jpg" 
              alt="Lotus Sport Logo" 
              className="h-10 w-auto rounded-lg shadow-md transition-transform duration-300 group-hover:scale-105" 
            />
            <div className="flex flex-col">
              <span className="text-2xl font-display font-bold tracking-wide text-white leading-none">
                LOTUS <span className="text-secondary-400">SPORT</span>
              </span>
              <span className="text-[0.7rem] text-primary-200 tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300 -mt-1 transform translate-y-1 group-hover:translate-y-0">
                Technology
              </span>
            </div>
          </Link>

          {/* DESKTOP MENÜ */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-4">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 text-lg font-medium transition-colors duration-300 group ${
                  isActive(item.path) ? 'text-secondary-400' : 'text-primary-100 hover:text-white'
                }`}
              >
                {item.label}
                {/* Alt Çizgi Animasyonu */}
                <span 
                  className={`absolute bottom-0 left-1/2 w-0 h-0.5 bg-secondary-400 transition-all duration-300 group-hover:w-4/5 group-hover:left-[10%] ${
                    isActive(item.path) ? 'w-4/5 left-[10%]' : ''
                  }`} 
                />
              </Link>
            ))}

            {/* Store Butonları Alanı */}
            <div className="pl-6 ml-4 border-l border-primary-700 flex items-center gap-3 relative group/download">
              
              {/* Hover Efekti ile Gelen Logo (Tooltip gibi) */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-6 w-40 p-4 bg-primary-800/95 backdrop-blur-xl rounded-2xl border border-secondary-500/20 opacity-0 invisible group-hover/download:opacity-100 group-hover/download:visible transition-all duration-500 transform translate-y-4 group-hover/download:translate-y-0 shadow-2xl flex flex-col items-center z-50">
                {/* Üçgen ok */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-primary-800 border-t border-l border-secondary-500/20 transform rotate-45"></div>
                
                <div className="relative w-16 h-16 mb-2 p-2 bg-white/5 rounded-full ring-1 ring-secondary-500/30 shadow-inner">
                   <img 
                    src="/exereyes_logo_whitebr.png" 
                    alt="ExerEyes App" 
                    className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]" 
                  />
                </div>
                <span className="text-xs font-bold text-white tracking-wider text-center">EXER<span className="text-secondary-400">EYES</span></span>
                <span className="text-[10px] text-primary-300 text-center mt-1">Google Play'de Yayında</span>
                <span className="text-[10px] text-primary-400 text-center mt-1">iOS için Yakında</span>
              </div>

              {/* Google Play Button */}
              <a 
                href="https://play.google.com/store/apps/details?id=com.exereyes.iuc"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white hover:text-black text-white transition-all duration-300 border border-white/20 hover:scale-110"
                title="Google Play'den İndir"
              >
                <svg viewBox="0 0 512 512" fill="currentColor" className="w-5 h-5">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                </svg>
              </a>

              {/* Apple Store Button */}
              <a 
                href="#" 
                onClick={handleStoreClick}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white hover:text-black text-white transition-all duration-300 border border-white/20 hover:scale-110"
                title="App Store'dan İndir (Yakında)"
              >
                <svg viewBox="0 0 384 512" fill="currentColor" className="w-5 h-5">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/>
                </svg>
              </a>

            </div>
          </div>

          {/* MOBİL MENÜ BUTONU */}
          <button
            className="lg:hidden relative z-50 p-2 text-primary-100 hover:text-white transition-colors focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>
      </div>

      {/* MOBİL MENÜ */}
      <div
        className={`fixed inset-0 z-40 lg:hidden bg-primary-900/95 backdrop-blur-xl transition-all duration-500 ease-in-out ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full space-y-8 p-8">
          {menuItems.map((item, index) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-2xl font-display font-medium tracking-wide transition-all duration-300 transform ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              } ${
                isActive(item.path) ? 'text-secondary-400' : 'text-white hover:text-secondary-300'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onClick={() => setIsMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          
          {/* Mobil İndirme Alanı */}
          <div 
            className={`flex flex-col items-center space-y-4 mt-8 transition-all duration-500 transform ${
              isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            <p className="text-primary-300 text-sm uppercase tracking-widest mb-2">Uygulamayı İndir</p>
            <div className="flex space-x-6">
               <a href="https://play.google.com/store/apps/details?id=com.exereyes.iuc" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center group">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/20 text-white group-hover:bg-white group-hover:text-black transition-colors">
                    <svg viewBox="0 0 512 512" fill="currentColor" className="w-6 h-6">
                      <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                    </svg>
                  </div>
                   <span className="text-xs text-primary-400 mt-2">Google Play</span>
               </a>
               <a href="#" onClick={handleStoreClick} className="flex flex-col items-center group">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/20 text-white group-hover:bg-white group-hover:text-black transition-colors">
                    <svg viewBox="0 0 384 512" fill="currentColor" className="w-6 h-6">
                      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/>
                    </svg>
                  </div>
                  <span className="text-xs text-primary-400 mt-2">App Store</span>
                  <span className="text-[10px] text-primary-500 mt-1">Yakında</span>
               </a>
            </div>
             <div className="mt-6 opacity-50">
               <img src="/exereyes_logo_whitebr.png" alt="Logo" className="w-16 h-16 object-contain" />
             </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-primary-950/50 to-transparent pointer-events-none"></div>
      </div>
    </nav>
  );
};

export default Navbar;